import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@feathr/components';
import { useAccount } from '@feathr/extender/state';
import { errorMessage } from '@feathr/hooks';
import type { TRachisEmpty } from '@feathr/rachis';
import { Collection, isWretchError, wretch } from '@feathr/rachis';

interface IBlackboxVersion {
  DB_ENV: string;
  ES_ENV: string;
  FLASK_ENV: string;
  GIT_COMMIT: string;
}

// TODO: Get this into context at some point
interface IUseGetBlackboxVersionReturnProps {
  /** The version of the blackbox */
  blackboxVersion: IBlackboxVersion | undefined;
  /** The error that occurred when fetching the blackbox version */
  blackboxVersionError: string | undefined;
  /** Resets the account to the minimal required data */
  handleReset: () => Promise<void>;
  /** Purges the event-based email campaign data from pinpoint for the account */
  handlePinpointPurge: () => Promise<void>;
  /** Resets and reseeds the account with default test data */
  handleReseed: () => Promise<void>;
  /** When blackboxVersion.DB_ENV and blackboxVersion.ES_ENV are set to 'development' */
  isOnDevelopment: boolean;
  /** When blackboxVersion.DB_ENV and blackboxVersion.ES_ENV are set to 'staging' */
  isOnStaging: boolean;
  /** When blackboxVersion.DB_ENV and blackboxVersion.ES_ENV are set to 'production' */
  isOnProduction: boolean;
}

function useBlackboxVersion(): IUseGetBlackboxVersionReturnProps {
  const { t } = useTranslation();
  const account = useAccount();

  const [blackboxVersion, setBlackboxVersion] = useState<IBlackboxVersion | undefined>();
  const [blackboxVersionError, setBlackboxVersionError] = useState<string | undefined>();

  const isOnDevelopment =
    blackboxVersion?.DB_ENV === 'development' && blackboxVersion?.ES_ENV === 'development';
  const isOnStaging =
    blackboxVersion?.DB_ENV === 'staging' && blackboxVersion?.ES_ENV === 'staging';
  const isOnProduction =
    blackboxVersion?.DB_ENV === 'production' && blackboxVersion?.ES_ENV === 'production';

  useEffect(() => {
    getBlackboxVersion().then(setBlackboxVersion);
  }, [setBlackboxVersion]);

  // Do not use wretch to have the most direct connection to the backend
  async function getBlackboxVersion(): Promise<IBlackboxVersion | undefined> {
    const response = await fetch(`${BLACKBOX_URL}version`, {
      method: 'GET',
      headers: Collection.prototype.getHeaders(),
    });
    if (!response.ok) {
      setBlackboxVersionError(await response.text());
      return;
    }
    try {
      return (await response.json()).data as IBlackboxVersion;
    } catch (error) {
      setBlackboxVersionError(errorMessage(error, t));
      return;
    }
  }

  async function handleReset(): Promise<void> {
    const response = await wretch<TRachisEmpty>(
      `${BLACKBOX_URL}accounts/${account.id}/purge-and-reset?with_data=0`,
      {
        headers: Collection.prototype.getHeaders(),
        method: 'POST',
      },
    );
    if (isWretchError(response)) {
      toast(t('An error occurred while trying to reset this account!'), { type: 'error' });
    }
  }

  async function handlePinpointPurge(): Promise<void> {
    const response = await wretch<TRachisEmpty>(
      `${BLACKBOX_URL}accounts/${account.id}/purge-pinpoint`,
      {
        headers: Collection.prototype.getHeaders(),
        method: 'POST',
      },
    );
    if (isWretchError(response)) {
      toast(t('An error occurred while trying to reset this account!'), { type: 'error' });
    }
  }

  async function handleReseed(): Promise<void> {
    const response = await wretch<TRachisEmpty>(
      `${BLACKBOX_URL}accounts/${account.id}/purge-and-reset?with_data=1`,
      {
        headers: Collection.prototype.getHeaders(),
        method: 'POST',
      },
    );
    if (isWretchError(response)) {
      toast(t('An error occurred while trying to reset and reseed this account!'), {
        type: 'error',
      });
    }
  }

  return {
    blackboxVersion,
    blackboxVersionError,
    handleReset,
    handlePinpointPurge,
    handleReseed,
    isOnDevelopment,
    isOnStaging,
    isOnProduction,
  };
}

export default useBlackboxVersion;
